import { css } from '@emotion/css';
import { Grid, TextField, MenuItem, Box, Chip, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

// Status filter options for mobile-service appointments. Lowercase to match the
// DB convention (per event-handlers/docs/appointment-lifecycle.md and the
// recent @hopdrive/sdk-appointments lowercase fix). Ordered by lifecycle.
const MOBILE_SERVICE_STATUS_FILTERS: Array<{
  value: string;
  label: string;
  color: 'default' | 'success' | 'warning' | 'info' | 'error';
}> = [
  { value: 'new', label: 'New', color: 'info' },
  { value: 'pending', label: 'Pending', color: 'warning' },
  { value: 'ready', label: 'Ready', color: 'success' },
  { value: 'completed', label: 'Completed', color: 'success' },
  { value: 'missed', label: 'Missed', color: 'error' },
  { value: 'canceled', label: 'Canceled', color: 'error' },
];

interface AppointmentsFilterRowProps {
  range: string;
  start: string;
  end: string;
  onRangeChange: (value: string) => void;
  onDateChange: (value: string, name: string) => void;
  disablePickers: boolean;
  search: string;
  setSearch: (value: string) => void;
  /** Multi-select array of currently-active status filters. Empty = no filter. */
  selectedStatuses: string[];
  setSelectedStatuses: (statuses: string[]) => void;
  /** Rendered first in the filter set (the tab's view switch). */
  leading?: React.ReactNode;
}

/** The Appointments tab's filter set: the view switch leads the date row, then the status chips. */
const AppointmentsFilterRow: React.FC<AppointmentsFilterRowProps> = ({
  range,
  start,
  end,
  onRangeChange,
  onDateChange,
  disablePickers,
  search,
  setSearch,
  selectedStatuses,
  setSelectedStatuses,
  leading,
}) => {
  const cls = useStyles();

  const handleRangeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRangeChange(event.target.value);
  };

  const handleDateChange = (name: string) => (value: dayjs.Dayjs | null) => {
    if (value) {
      onDateChange(value.format(), name);
    }
  };

  const toggleStatus = (status: string) => {
    if (selectedStatuses.includes(status)) {
      setSelectedStatuses(selectedStatuses.filter(s => s !== status));
    } else {
      setSelectedStatuses([...selectedStatuses, status]);
    }
  };

  const clearStatuses = () => setSelectedStatuses([]);

  return (
    <div className={cls.main}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2} alignItems='center'>
          {leading ? <Grid size='auto'>{leading}</Grid> : null}
          <Grid size={{ xs: 12, sm: 'grow' }}>
            <TextField
              select
              fullWidth
              label='Date Range'
              variant='outlined'
              margin='none'
              value={range}
              onChange={handleRangeChange}
            >
              <MenuItem value='today'>Today</MenuItem>
              <MenuItem value='thisWeek'>This Week</MenuItem>
              <MenuItem value='nextWeek'>Next Week</MenuItem>
              <MenuItem value='thisMonth'>This Month</MenuItem>
              <MenuItem value='thisYear'>This Year</MenuItem>
              <MenuItem value='custom'>Custom</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{ xs: 6, sm: 3 }}>
            <DatePicker
              disabled={disablePickers}
              label='Start Date'
              value={dayjs(start)}
              onChange={handleDateChange('start')}
              format='MM/DD/YYYY'
              slotProps={{
                textField: { fullWidth: true, variant: 'outlined', size: 'small' },
              }}
            />
          </Grid>

          <Grid size={{ xs: 6, sm: 3 }}>
            <DatePicker
              disabled={disablePickers}
              label='End Date'
              value={dayjs(end)}
              onChange={handleDateChange('end')}
              format='MM/DD/YYYY'
              slotProps={{
                textField: { fullWidth: true, variant: 'outlined', size: 'small' },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label='Search'
              placeholder='Search by ID, customer name, phone, vehicle, status...'
              variant='outlined'
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>

      {/* Status filter chips — Hopdrive data-density pattern: below the toolbar
          inputs, gap 8px, size small, radius 6px. Multi-select toggle. */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 2 }}>
        <Typography
          variant='overline'
          sx={{ fontWeight: 600, color: 'grey.500', letterSpacing: 0.5, mr: 0.5 }}
        >
          Status
        </Typography>
        {MOBILE_SERVICE_STATUS_FILTERS.map(({ value, label, color }) => {
          const active = selectedStatuses.includes(value);
          return (
            <Chip
              key={value}
              label={label}
              size='small'
              color={active ? color : 'default'}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => toggleStatus(value)}
              sx={{ borderRadius: '6px', fontWeight: 500 }}
            />
          );
        })}
        {selectedStatuses.length > 0 && (
          <Chip
            label='Clear'
            size='small'
            variant='outlined'
            onClick={clearStatuses}
            sx={{ borderRadius: '6px', borderStyle: 'dashed', color: 'text.secondary' }}
          />
        )}
      </Box>
    </div>
  );
};

const useStyles = () => ({
  main: css`
    margin-bottom: 16px;
  `,
});

export default AppointmentsFilterRow;
